class GoogleVisionImageAnalyzer {
  constructor() {
    this.apiKey = process.env.REACT_APP_GOOGLE_VISION_API_KEY || window.GOOGLE_VISION_API_KEY;
    this.apiBase = 'https://vision.googleapis.com/v1';
    this.isAnalyzing = false;
    this.requestCount = 0;
    
    // Enhanced debugging
    console.log('🔍 GoogleVisionImageAnalyzer initialized:', {
      apiKeyPresent: !!this.apiKey,
      apiKeyPrefix: this.apiKey ? this.apiKey.substring(0, 12) + '...' : 'None',
      apiBase: this.apiBase,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Convert image file to base64 data URL
   */
  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      console.log('🔄 Converting image to base64:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type
      });
      
      const reader = new FileReader();
      
      reader.onload = () => {
        const result = reader.result;
        // Remove data URL prefix for Google Vision API
        const base64Data = result.split(',')[1];
        console.log('✅ Base64 conversion successful:', {
          originalLength: result.length,
          base64Length: base64Data.length
        });
        resolve(base64Data);
      };
      
      reader.onerror = (error) => {
        console.error('❌ Base64 conversion failed:', error);
        reject(new Error(`Base64 conversion failed: ${error}`));
      };
      
      reader.readAsDataURL(file);
    });
  }

  /**
   * Analyze image using Google Cloud Vision API
   */
  async analyzeImage(imageFile, context = {}) {
    const debugId = Math.random().toString(36).substring(7);
    console.log(`🚀 [${debugId}] Starting Google Vision analysis`);
    
    if (this.isAnalyzing) {
      throw new Error("Analysis already in progress");
    }

    try {
      this.isAnalyzing = true;
      this.requestCount++;
      
      // Step 1: Validate inputs
      console.log(`📋 [${debugId}] Input validation:`, {
        filePresent: !!imageFile,
        fileName: imageFile?.name,
        fileSize: imageFile?.size,
        fileType: imageFile?.type,
        contextKeys: Object.keys(context),
        apiKeyPresent: !!this.apiKey,
        requestCount: this.requestCount
      });

      if (!this.apiKey) {
        throw new Error("Google Vision API key not configured");
      }

      // Step 2: Convert image to base64
      console.log(`🖼️ [${debugId}] Converting image to base64...`);
      const base64Image = await this.fileToBase64(imageFile);
      
      // Step 3: Prepare Google Vision API request
      const requestPayload = {
        requests: [
          {
            image: {
              content: base64Image
            },
            features: [
              {
                type: "LABEL_DETECTION",
                maxResults: 20
              },
              {
                type: "OBJECT_LOCALIZATION",
                maxResults: 20
              },
              {
                type: "TEXT_DETECTION",
                maxResults: 10
              }
            ]
          }
        ]
      };

      console.log(`📤 [${debugId}] Request payload prepared for Google Vision API`);

      // Step 4: Make API call
      console.log(`🌐 [${debugId}] Making API call to Google Vision...`);
      
      const startTime = Date.now();
      const response = await fetch(`${this.apiBase}/images:annotate?key=${this.apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestPayload)
      });
      const endTime = Date.now();

      console.log(`📥 [${debugId}] API response received:`, {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        responseTime: `${endTime - startTime}ms`
      });

      // Step 5: Handle response
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(`❌ [${debugId}] Google Vision API Error:`, {
          status: response.status,
          statusText: response.statusText,
          errorData: errorData
        });
        
        throw new Error(`Google Vision API Error: ${response.status} - ${errorData.error?.message || response.statusText}`);
      }

      const data = await response.json();
      console.log(`✅ [${debugId}] Google Vision API Success:`, {
        hasResponses: !!data.responses,
        responseCount: data.responses?.length
      });
      
      if (!data.responses || !data.responses[0]) {
        console.error(`❌ [${debugId}] Invalid response structure:`, data);
        throw new Error("Invalid Google Vision API response format");
      }

      const visionData = data.responses[0];
      
      // Step 6: Process Vision API results
      const labels = visionData.labelAnnotations || [];
      const objects = visionData.localizedObjectAnnotations || [];
      const texts = visionData.textAnnotations || [];

      console.log(`🔍 [${debugId}] Vision analysis results:`, {
        labelCount: labels.length,
        objectCount: objects.length,
        textCount: texts.length,
        topLabels: labels.slice(0, 5).map(l => l.description),
        topObjects: objects.slice(0, 5).map(o => o.name)
      });

      // Step 7: Generate construction-focused description
      const description = this.generateConstructionDescription(labels, objects, texts, context);
      
      console.log(`📝 [${debugId}] Generated description:`, {
        description: description,
        length: description.length,
        wordCount: description.split(' ').length
      });
      
      return {
        success: true,
        description: description,
        confidence: this.assessConfidence(labels, objects),
        timestamp: new Date().toISOString(),
        debugId: debugId,
        visionData: {
          labels: labels.slice(0, 10),
          objects: objects.slice(0, 10),
          texts: texts.slice(0, 3)
        },
        requestCount: this.requestCount
      };

    } catch (error) {
      console.error(`❌ [${debugId}] Analysis failed:`, {
        errorMessage: error.message,
        errorStack: error.stack,
        fileName: imageFile?.name,
        timestamp: new Date().toISOString()
      });
      
      return {
        success: false,
        error: error.message,
        fallbackDescription: this.generateFallbackDescription(imageFile),
        timestamp: new Date().toISOString(),
        debugId: debugId,
        requestCount: this.requestCount
      };
    } finally {
      this.isAnalyzing = false;
      console.log(`🏁 [${debugId}] Analysis complete`);
    }
  }

  /**
   * Generate construction-focused description from Vision API results
   */
  generateConstructionDescription(labels, objects, texts, context = {}) {
    const {
      projectType = "construction",
      sectionName = "",
      reportTitle = ""
    } = context;

    // Construction-related terms mapping
    const constructionTerms = {
      // Materials
      'concrete': 'concrete',
      'steel': 'steel',
      'wood': 'lumber',
      'brick': 'masonry',
      'metal': 'metal framework',
      'pipe': 'piping',
      'wire': 'electrical wiring',
      'cable': 'electrical cable',
      
      // Equipment
      'crane': 'construction crane',
      'excavator': 'excavation equipment',
      'truck': 'construction vehicle',
      'ladder': 'access ladder',
      'scaffold': 'scaffolding',
      'tool': 'construction tools',
      
      // Structures
      'building': 'building structure',
      'wall': 'wall construction',
      'floor': 'floor system',
      'ceiling': 'ceiling work',
      'roof': 'roofing',
      'foundation': 'foundation work',
      'beam': 'structural beam',
      'column': 'support column',
      
      // Safety
      'helmet': 'safety helmet',
      'vest': 'safety vest',
      'sign': 'safety signage'
    };

    // Extract construction-relevant labels
    const relevantLabels = labels
      .filter(label => label.score > 0.6)
      .map(label => {
        const term = label.description.toLowerCase();
        return constructionTerms[term] || term;
      })
      .filter(term => this.isConstructionRelevant(term))
      .slice(0, 5);

    // Extract construction-relevant objects
    const relevantObjects = objects
      .filter(obj => obj.score > 0.5)
      .map(obj => {
        const term = obj.name.toLowerCase();
        return constructionTerms[term] || term;
      })
      .filter(term => this.isConstructionRelevant(term))
      .slice(0, 3);

    // Combine all relevant terms
    const allTerms = [...new Set([...relevantLabels, ...relevantObjects])];

    // Generate description based on detected elements
    let description = "";

    if (allTerms.length === 0) {
      description = `${projectType} documentation photo`;
    } else {
      // Categorize terms
      const materials = allTerms.filter(term => 
        ['concrete', 'steel', 'lumber', 'masonry', 'metal', 'piping', 'electrical'].some(mat => term.includes(mat))
      );
      
      const equipment = allTerms.filter(term => 
        ['crane', 'excavation', 'vehicle', 'tools', 'scaffolding', 'ladder'].some(eq => term.includes(eq))
      );
      
      const structures = allTerms.filter(term => 
        ['building', 'wall', 'floor', 'ceiling', 'roof', 'foundation', 'beam', 'column'].some(str => term.includes(str))
      );

      // Build description
      if (structures.length > 0) {
        description = `${structures[0]} showing`;
        if (materials.length > 0) {
          description += ` ${materials.slice(0, 2).join(' and ')}`;
        }
        if (equipment.length > 0) {
          description += ` with ${equipment[0]} visible`;
        }
      } else if (materials.length > 0) {
        description = `${materials[0]} installation`;
        if (materials.length > 1) {
          description += ` with ${materials[1]}`;
        }
        if (equipment.length > 0) {
          description += ` and ${equipment[0]}`;
        }
      } else if (equipment.length > 0) {
        description = `Construction work featuring ${equipment.slice(0, 2).join(' and ')}`;
      } else {
        description = `${projectType} progress documentation showing ${allTerms.slice(0, 3).join(', ')}`;
      }
    }

    // Add section context if available
    if (sectionName) {
      description = `${sectionName}: ${description}`;
    }

    // Ensure proper capitalization and punctuation
    description = description.charAt(0).toUpperCase() + description.slice(1);
    if (!description.endsWith('.')) {
      description += '.';
    }

    return description;
  }

  /**
   * Check if a term is construction-relevant
   */
  isConstructionRelevant(term) {
    const constructionKeywords = [
      'concrete', 'steel', 'wood', 'lumber', 'brick', 'masonry', 'metal', 'pipe', 'piping',
      'wire', 'wiring', 'electrical', 'cable', 'crane', 'excavator', 'truck', 'vehicle',
      'ladder', 'scaffold', 'scaffolding', 'tool', 'tools', 'building', 'structure',
      'wall', 'floor', 'ceiling', 'roof', 'roofing', 'foundation', 'beam', 'column',
      'helmet', 'safety', 'vest', 'sign', 'signage', 'construction', 'work', 'site',
      'equipment', 'machinery', 'installation', 'framework', 'support', 'structural'
    ];

    return constructionKeywords.some(keyword => 
      term.toLowerCase().includes(keyword) || keyword.includes(term.toLowerCase())
    );
  }

  /**
   * Assess confidence based on Vision API results
   */
  assessConfidence(labels, objects) {
    const avgLabelScore = labels.length > 0 ? 
      labels.reduce((sum, label) => sum + label.score, 0) / labels.length : 0;
    
    const avgObjectScore = objects.length > 0 ? 
      objects.reduce((sum, obj) => sum + obj.score, 0) / objects.length : 0;
    
    const overallScore = (avgLabelScore + avgObjectScore) / 2;
    
    // Adjust confidence based on construction relevance
    const constructionRelevantCount = labels.filter(label => 
      this.isConstructionRelevant(label.description)
    ).length;
    
    let confidence = overallScore;
    if (constructionRelevantCount >= 3) confidence += 0.1;
    if (constructionRelevantCount >= 5) confidence += 0.1;
    
    return Math.min(confidence, 1.0);
  }

  /**
   * Generate fallback description when analysis fails
   */
  generateFallbackDescription(imageFile) {
    const fileName = imageFile.name.toLowerCase();
    const fileSize = (imageFile.size / 1024 / 1024).toFixed(1);
    
    if (fileName.includes('before')) return `Before condition photo (${fileSize}MB)`;
    if (fileName.includes('after')) return `After condition photo (${fileSize}MB)`;
    if (fileName.includes('progress')) return `Construction progress photo (${fileSize}MB)`;
    if (fileName.includes('issue') || fileName.includes('problem')) return `Issue documentation photo (${fileSize}MB)`;
    if (fileName.includes('complete')) return `Completed work photo (${fileSize}MB)`;
    
    return `Project documentation photo (${fileSize}MB)`;
  }

  /**
   * Check if API is configured
   */
  isConfigured() {
    const configured = !!this.apiKey;
    console.log('🔍 Configuration check:', {
      configured: configured,
      apiKeyPresent: !!this.apiKey,
      apiBase: this.apiBase,
      requestCount: this.requestCount
    });
    return configured;
  }

  /**
   * Get detailed status
   */
  getStatus() {
    return {
      configured: this.isConfigured(),
      analyzing: this.isAnalyzing,
      apiKey: this.apiKey ? `${this.apiKey.substring(0, 12)}...` : 'Not configured',
      apiBase: this.apiBase,
      requestCount: this.requestCount,
      service: 'Google Cloud Vision API',
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Test API connection
   */
  async testConnection() {
    console.log('🧪 Testing Google Vision API connection...');
    
    try {
      // Create a simple test image (1x1 pixel PNG)
      const testImageBase64 = 'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChAI9jU77yQAAAABJRU5ErkJggg==';
      
      const response = await fetch(`${this.apiBase}/images:annotate?key=${this.apiKey}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          requests: [{
            image: { content: testImageBase64 },
            features: [{ type: "LABEL_DETECTION", maxResults: 1 }]
          }]
        })
      });

      console.log('🌐 Connection test response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Google Vision connection successful');
        
        return {
          success: true,
          service: 'Google Cloud Vision API',
          features: ['Label Detection', 'Object Localization', 'Text Detection'],
          requestCount: this.requestCount + 1
        };
      } else {
        const errorText = await response.text();
        console.error('❌ Connection test failed:', errorText);
        return {
          success: false,
          error: `${response.status}: ${errorText}`,
          requestCount: this.requestCount
        };
      }
    } catch (error) {
      console.error('❌ Connection test error:', error);
      return {
        success: false,
        error: error.message,
        requestCount: this.requestCount
      };
    }
  }
}

export default GoogleVisionImageAnalyzer;

